import React from 'react';

import PeopleDelta from 'Components/people/PeopleDelta';

import { formatFigure, formatSeparateByThousands, formatToFixedMoneyWithLocale, ZERO_DASH } from 'Helpers/formatService';

/**
 * What has moved per child level since the compare-to version.
 *
 * `hasComparison` is derived from `movement` itself (`null` vs an array) rather
 * than taken as a separate prop read from live control state. The two used to
 * disagree for the duration of an in-flight refetch — `controls` updates the
 * instant the user changes a selector, but `movement` still holds the previous
 * completed payload until the response lands — so this panel is now a pure
 * function of the one object it renders from.
 *
 * `scopeMissing` is the other case `movement: []` can mean: a version WAS
 * selected but has no resolvable equivalent of this department/cost centre at
 * all (renamed or deleted there). Without it, that state would render either
 * as "nothing is being compared" (wrong — a version IS selected) or as a table
 * of unexplained em dashes (the payload's own doc on
 * `PeopleDashboardService::movement()` covers why that reads worse than
 * showing nothing).
 *
 * A child with no counterpart in the compared version shows an em dash in every
 * column, never a full-value delta: a cost centre that did not exist there has
 * not grown by its whole headcount, it is simply not comparable.
 *
 * Every delta is neutral charcoal. More headcount in a plan is neither good nor
 * bad, and colouring it would editorialise a figure the reader is still
 * interpreting.
 */

const COUNT = (value) => formatFigure(value, formatSeparateByThousands, { precision: 3 });
const MONEY = (value) => formatFigure(value, formatToFixedMoneyWithLocale);

const MEASURES = [
    { key: 'positions_delta', label: () => $t('Positions'), width: 'tw-w-32', format: COUNT },
    { key: 'peak_headcount_delta', label: () => $t('Peak'), width: 'tw-w-32', format: COUNT },
    { key: 'cost_delta', label: () => $t('Workforce cost'), width: 'tw-w-40', format: MONEY, money: true },
    { key: 'committed_delta', label: () => $t('Committed'), width: 'tw-w-40', format: MONEY, money: true },
];

function MovementCell({ measure, row }) {
    const delta = row[measure.key];

    if (typeof delta !== 'number') {
        return <td className="inbud-numeric-cell tw-text-charcoal-25">—</td>;
    }

    if (delta === 0) {
        return <td className="inbud-numeric-cell">{ZERO_DASH}</td>;
    }

    return (
        <td className="inbud-numeric-cell">
            <PeopleDelta delta={delta} format={measure.format} />
        </td>
    );
}

function childHeader(mode) {
    if (mode === 'department') {
        return 'Department';
    }

    return mode === 'cohort' ? $t('Role') : $t('Cost centre');
}

export default function PeopleMovement({ movement, mode, scopeMissing }) {
    const hasComparison = movement !== null;
    const rows = movement ?? [];

    // Three different nothings, and each needs its own sentence: no version
    // picked yet; a version picked but it has nothing to compare THIS SCOPE
    // against at all; and a version picked with genuinely nothing on either
    // side of a scope that does resolve.
    if (!hasComparison) {
        return <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">{$t('Select a version to compare against to see what has moved.')}</p>;
    }

    if (scopeMissing) {
        return (
            <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">
                {$t('This department or cost centre has no equivalent in the compared version, so movement cannot be measured.')}
            </p>
        );
    }

    if (rows.length === 0) {
        return <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">{$t('Neither this version nor the compared one holds any positions here.')}</p>;
    }

    // `key in row`, not truthiness: money deltas are omitted, not nulled, for a
    // user without pay-rate visibility — and a null delta is a real answer that
    // must keep its column.
    const measures = MEASURES.filter((measure) => !measure.money || measure.key in rows[0]);

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table inbud-table_first-sticky tw-table-fixed">
                <thead>
                    <tr>
                        {/* The child level differs by scope: departments beneath the
                            event, cost centres beneath a department, cohorts beneath
                            a cost centre. */}
                        <th className="tw-w-56">{childHeader(mode)}</th>
                        {measures.map((measure) => (
                            <th key={measure.key} className={measure.width}>
                                {measure.label()}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key}>
                            <td>
                                <div className="tw-truncate">{row.label}</div>
                                {/* A child that exists only in the compared version — the
                                    largest movement there is, and invisible without saying so. */}
                                {row.positions === 0 && <div className="tw-text-xs tw-text-charcoal-50">{$t('no longer in this version')}</div>}
                            </td>
                            {measures.map((measure) => (
                                <MovementCell key={measure.key} measure={measure} row={row} />
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
